import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { httpClient } from '../../utils/apiCall';
import { handleApiError } from '../../utils/apiError';
import { API_ENDPOINTS } from '../../config/api';

// Type Definitions
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: string[];
  traceId?: string;
}

export interface UserDashboardData {
  totalRequests: number;
  draftRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  recentRequests: number;
}

export interface AdminDashboardData {
  pendingApprovalRequests: number;
  totalUsers: number;
  totalRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  recentRequests: number;
  recentApprovedRequests: number;
  activeSuppliers: number;
}

export interface PurchaserDashboardData {
  approvedRequestsForQuote: number;
  readyToEvaluateRequests: number;
  activeQuotes: number;
  negotiationQuoteItems: number;
  awardedQuoteItems: number;
  waitingPurchaseOrders: number;
  syncedPurchaseOrders: number;
  recentQuotes: number;
}

interface DashboardState {
  loading: boolean;
  error: string | null;
  userDashboard: UserDashboardData | null;
  adminDashboard: AdminDashboardData | null;
  purchaserDashboard: PurchaserDashboardData | null;
}

const initialState: DashboardState = {
  loading: false,
  error: null,
  userDashboard: null,
  adminDashboard: null,
  purchaserDashboard: null,
};

// Async Thunks
export const fetchUserDashboard = createAsyncThunk(
  'dashboard/fetchUserDashboard',
  async (_, thunkAPI) => {
    try {
      const response = await httpClient.get<UserDashboardData>(
        API_ENDPOINTS.DASHBOARD.USER
      );

      if (!response.success) {
        throw new Error(response.message || 'Dashboard verisi alınamadı');
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        handleApiError(error, 'Dashboard verisi alınamadı')
      );
    }
  }
);

export const fetchAdminDashboard = createAsyncThunk(
  'dashboard/fetchAdminDashboard',
  async (_, thunkAPI) => {
    try {
      const response = await httpClient.get<AdminDashboardData>(
        API_ENDPOINTS.DASHBOARD.ADMIN
      );

      if (!response.success) {
        throw new Error(response.message || 'Dashboard verisi alınamadı');
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        handleApiError(error, 'Dashboard verisi alınamadı')
      );
    }
  }
);

export const fetchPurchaserDashboard = createAsyncThunk(
  'dashboard/fetchPurchaserDashboard',
  async (_, thunkAPI) => {
    try {
      const response = await httpClient.get<PurchaserDashboardData>(
        API_ENDPOINTS.DASHBOARD.PURCHASER
      );

      if (!response.success) {
        throw new Error(response.message || 'Dashboard verisi alınamadı');
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        handleApiError(error, 'Dashboard verisi alınamadı')
      );
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDashboard: (state) => {
      state.userDashboard = null;
      state.adminDashboard = null;
      state.purchaserDashboard = null;
      state.error = null;
    },
  },
  extraReducers(builder) {
    // User Dashboard
    builder.addCase(fetchUserDashboard.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserDashboard.fulfilled, (state, action) => {
      state.loading = false;
      state.userDashboard = action.payload;
      state.error = null;
    });
    builder.addCase(fetchUserDashboard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Admin Dashboard
    builder.addCase(fetchAdminDashboard.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAdminDashboard.fulfilled, (state, action) => {
      state.loading = false;
      state.adminDashboard = action.payload;
      state.error = null;
    });
    builder.addCase(fetchAdminDashboard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Purchaser Dashboard
    builder.addCase(fetchPurchaserDashboard.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPurchaserDashboard.fulfilled, (state, action) => {
      state.loading = false;
      state.purchaserDashboard = action.payload;
      state.error = null;
    });
    builder.addCase(fetchPurchaserDashboard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, clearDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
